import type { EniOdbKind, EniRole, Level, OdbKind, OdbNode, OdbSummary } from "../../api/types";
import { ACTIVE, ENEMY_KINDS, FRIENDLY_KINDS } from "../../echelons";
import { tc } from "../../i18n/i18n";

/** Ordre de bataille ennemi construit (docs/14 §7) : fonctions pures de l'éditeur et des Données initiales. Les
 *  codes (« 11(EN)CIERECO ») et les rôles par défaut sont calculés par le serveur (`/odbs/preview`) : le client les
 *  affiche, il ne les invente pas. */

/** Nature d'ordre de bataille ennemi qui fait face à chaque niveau d'ordre (table des échelons, registre B47). Un
 *  niveau sans ennemi propre affronte celui du niveau au-dessus : la compagnie (docs/21 §4), une partie de l'ennemi de
 *  son GTIA, un bataillon. */
export const ENI_KIND_BY_LEVEL = Object.fromEntries(
  ACTIVE.map((e) => [e.key, e.eni_odb_kind ?? ACTIVE.find((up) => up.child === e.key)?.eni_odb_kind]),
) as Record<Exclude<Level, "CENTAC">, EniOdbKind>;

export const ENI_ROLES: readonly EniRole[] = ["reco", "ech1", "ech2", "renforts", "appuis", "soutien", "commandement"];

export function isEniKind(kind: string | null | undefined): kind is EniOdbKind {
  return Boolean(kind && (ENEMY_KINDS as readonly string[]).includes(kind));
}

/** Nature d'un ordre de bataille pour l'éditeur : une division ennemie du catalogue (« eni_division ») se lit
 *  comme une division ennemie construite. */
export function editorKind(kind: string | null | undefined): OdbKind | null {
  if (kind === "eni_division") return "eni_div";
  return kind && ((FRIENDLY_KINDS as readonly string[]).includes(kind) || isEniKind(kind)) ? (kind as OdbKind) : null;
}

/** Nature ennemie qui fait face au niveau de l'ordre (aucune en CENTAC). */
export function eniKindFor(level: Level | null | undefined): EniOdbKind | null {
  return level && level !== "CENTAC" ? ENI_KIND_BY_LEVEL[level] : null;
}

/** Ordre de bataille ennemi générique (sans faction, codes « (EN) ») : drapeau `generic` du serveur ; un serveur
 *  qui ne le donne pas encore le dit par la faction « EN ». */
export function isGenericEniOdb(odb: Pick<OdbSummary, "generic" | "faction">): boolean {
  return odb.generic ?? odb.faction === "EN";
}

/** Ordres de bataille ennemis de la licence qui font face au niveau de l'ordre ; face à l'ennemi générique (force
 *  non française, `neutral`, registre B39), les seuls ordres de bataille ennemis génériques. */
export function eniOdbsFor(odbs: readonly OdbSummary[], level: Level | null | undefined, neutral = false): OdbSummary[] {
  const kind = eniKindFor(level);
  return kind ? odbs.filter((o) => o.kind === kind && (!neutral || isGenericEniOdb(o))) : [];
}

/** Libellé d'un rôle ennemi dans la langue de l'interface (« Premier échelon », « Fire support »). */
export function eniRoleLabel(role: EniRole | null | undefined): string {
  switch (role) {
    case "reco":
      return tc("rôle", "Reconnaissance");
    case "ech1":
      return tc("rôle", "Premier échelon");
    case "ech2":
      return tc("rôle", "Second échelon");
    case "renforts":
      return tc("rôle", "Renforts");
    case "appuis":
      return tc("rôle", "Appuis");
    case "soutien":
      return tc("rôle", "Soutien");
    case "commandement":
      return tc("rôle", "Commandement");
    default:
      return "";
  }
}

/** Unité à un chemin, ou null (fonction de l'arbre de l'éditeur). */
export { nodeAtOrNull } from "./tree";

/** Sous-arbre d'une division ennemie du catalogue que le serveur propose de dupliquer seul. */
export type EniSubtree = NonNullable<OdbSummary["subtrees"]>[number];

/** Une ligne d'une division ennemie du catalogue se duplique seule si le serveur la range parmi ses sous-arbres
 *  (`subtrees`) : brigade ou bataillon dont les subordonnés tiennent les emplacements de la manœuvre qu'il
 *  affronte. Les brigades du catalogue, sans subordonnés, n'y sont pas : copiées seules, elles laisseraient
 *  l'ennemi de référence sur la carte. */
export function duplicableEniUnit(code: string | null | undefined, subtrees: OdbSummary["subtrees"]): boolean {
  return Boolean(code) && (subtrees ?? []).some((s) => s.code === code);
}

/** Sous-arbres des divisions du catalogue d'une faction qui se dupliquent seuls à la nature demandée (« Nouvel
 *  ordre de bataille ennemi »), avec leur division, dans l'ordre du catalogue. */
export function eniSubtreesFor(
  catalog: readonly OdbSummary[],
  faction: string,
  kind: EniOdbKind,
): { division: OdbSummary; subtree: EniSubtree }[] {
  return catalog
    .filter((d) => d.faction === faction)
    .flatMap((division) => (division.subtrees ?? []).filter((s) => s.kind === kind).map((subtree) => ({ division, subtree })));
}

/** Arbre de départ d'un ordre de bataille ennemi (réponse de `/odbs/starter`) nommé et rattaché à sa faction. */
export function namedStarter(tree: OdbNode, name: string, faction: string): OdbNode {
  return { ...tree, name, faction };
}
